<script lang="ts">
	// Every catalogue page's frame: its own header, apart from the website's,
	// with the mark, the catalogue's name and its main menu (NavMenu), then the
	// page. Drawn by the worker; only the menu's button runs in the browser.
	// The header stays at the top of the page while it scrolls, except on a
	// restaurant's page (C2), where the menu filter's bar stays instead.

	import type { Snippet } from 'svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import { brand } from './copy';
	import NavMenu from './NavMenu.svelte';

	interface Props {
		page: 'home' | 'restaurant' | 'down';
		ownersHref: string;
		children: Snippet;
	}

	let { page, ownersHref, children }: Props = $props();

	const menu = $derived({ current: page === 'restaurant' ? 'restaurant' : 'home', ownersHref });
</script>

<div class="shell" data-page={page}>
	<header class="header" class:pinned={page !== 'restaurant'}>
		<div class="bar">
			<a class="brand" href="/" data-testid="catalogue-brand">
				<BrandMark />
				<span class="names">
					<span class="name">{brand.name}</span>
					<span class="tagline">{brand.tagline}</span>
				</span>
			</a>
			<span class="space"></span>
			<div class="nav" data-island="nav" data-props={JSON.stringify(menu)}>
				<NavMenu current={menu.current as 'home' | 'restaurant'} {ownersHref} />
			</div>
		</div>
	</header>
	<main class="main">
		{@render children()}
	</main>
</div>

<style>
	.shell {
		min-block-size: 100vh;
		display: flex;
		flex-direction: column;
		background: var(--surface-page);
		color: var(--text-strong);
	}

	.header {
		background: var(--surface-page);
		border-block-end: var(--border-width) solid var(--border-default);
	}

	.pinned {
		position: sticky;
		inset-block-start: 0;
		z-index: 10;
	}

	.bar {
		max-inline-size: var(--page-max);
		margin-inline: auto;
		padding: var(--space-5) var(--page-gutter);
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
	}

	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		color: var(--text-strong);
		text-decoration: none;
	}

	.names {
		display: flex;
		flex-direction: column;
		line-height: var(--leading-tight);
	}

	.name {
		font-size: var(--text-md);
		font-weight: var(--weight-bold);
	}

	.tagline {
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.space {
		flex: 1;
	}

	.nav {
		display: contents;
	}

	/* A phone keeps the header to one short row: a step less padding, by
	   Roman's word on #614. Its width is NavMenu's. */
	@media (max-width: 639.98px) {
		.bar {
			padding-block: var(--space-4);
		}

		/* The brand may shrink to nothing but grows no further than its names,
		   so on a very narrow phone, or with a large system font, they take an
		   ellipsis and the button stays on the brand's row rather than
		   wrapping under it, and the empty strip beside the names is not part
		   of the link. */
		.brand {
			flex: 1 1 0;
			max-inline-size: max-content;
			margin-inline-end: auto;
		}

		.space {
			display: none;
		}

		.brand,
		.names {
			min-inline-size: 0;
		}

		.name,
		.tagline {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}

	.main {
		flex: 1;
		inline-size: 100%;
		max-inline-size: var(--page-max);
		margin-inline: auto;
		padding: var(--space-11) var(--page-gutter) var(--space-12);
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}
</style>
